// SPDX-FileCopyrightText: 2017-2026 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

// GENERATED FILE: no manual modifications

import type { Action } from '../action'
import type { AssistanceActionResponse } from './assistanceaction'
import type { AssistanceFactorId } from './shared'
import type { DaycareAssistanceId } from './shared'
import type { EvakaUser } from './user'
import FiniteDateRange from '../../finite-date-range'
import HelsinkiDateTime from '../../helsinki-date-time'
import type { JsonOf } from '../../json'
import type { OtherAssistanceMeasureId } from './shared'
import type { PersonId } from './shared'
import type { PreschoolAssistanceId } from './shared'
import { deserializeJsonAssistanceActionResponse } from './assistanceaction'

/**
* Generated from evaka.core.assistance.AssistanceFactor
*/
export interface AssistanceFactor {
  capacityFactor: number
  childId: PersonId
  id: AssistanceFactorId
  modifiedAt: HelsinkiDateTime
  modifiedBy: EvakaUser
  validDuring: FiniteDateRange
}

/**
* Generated from evaka.core.assistance.AssistanceController.AssistanceFactorResponse
*/
export interface AssistanceFactorResponse {
  data: AssistanceFactor
  permittedActions: Action.AssistanceFactor[]
}

/**
* Generated from evaka.core.assistance.AssistanceFactorUpdate
*/
export interface AssistanceFactorUpdate {
  capacityFactor: number
  validDuring: FiniteDateRange
}

/**
* Generated from evaka.core.assistance.AssistanceController.AssistanceResponse
*/
export interface AssistanceResponse {
  assistanceActions: AssistanceActionResponse[]
  assistanceFactors: AssistanceFactorResponse[]
  daycareAssistances: DaycareAssistanceResponse[]
  otherAssistanceMeasures: OtherAssistanceMeasureResponse[]
  preschoolAssistances: PreschoolAssistanceResponse[]
}

/**
* Generated from evaka.core.assistance.DaycareAssistance
*/
export interface DaycareAssistance {
  childId: PersonId
  id: DaycareAssistanceId
  level: DaycareAssistanceLevel
  modified: HelsinkiDateTime
  modifiedBy: EvakaUser
  validDuring: FiniteDateRange
}

/**
* Generated from evaka.core.assistance.DaycareAssistanceLevel
*/
export const daycareAssistanceLevels = [
  'GENERAL_SUPPORT',
  'GENERAL_SUPPORT_WITH_DECISION',
  'INTENSIFIED_SUPPORT',
  'SPECIAL_SUPPORT'
] as const

export type DaycareAssistanceLevel = typeof daycareAssistanceLevels[number]

/**
* Generated from evaka.core.assistance.AssistanceController.DaycareAssistanceResponse
*/
export interface DaycareAssistanceResponse {
  data: DaycareAssistance
  permittedActions: Action.DaycareAssistance[]
}

/**
* Generated from evaka.core.assistance.DaycareAssistanceUpdate
*/
export interface DaycareAssistanceUpdate {
  level: DaycareAssistanceLevel
  validDuring: FiniteDateRange
}

/**
* Generated from evaka.core.assistance.OtherAssistanceMeasure
*/
export interface OtherAssistanceMeasure {
  childId: PersonId
  id: OtherAssistanceMeasureId
  modified: HelsinkiDateTime
  modifiedBy: EvakaUser
  type: OtherAssistanceMeasureType
  validDuring: FiniteDateRange
}

/**
* Generated from evaka.core.assistance.AssistanceController.OtherAssistanceMeasureResponse
*/
export interface OtherAssistanceMeasureResponse {
  data: OtherAssistanceMeasure
  permittedActions: Action.OtherAssistanceMeasure[]
}

/**
* Generated from evaka.core.assistance.OtherAssistanceMeasureType
*/
export const otherAssistanceMeasureTypes = [
  'TRANSPORT_BENEFIT',
  'ACCULTURATION_SUPPORT',
  'ANOMALOUS_EDUCATION_START',
  'CHILD_DISCUSSION_OFFERED',
  'CHILD_DISCUSSION_HELD',
  'CHILD_DISCUSSION_COUNSELING'
] as const

export type OtherAssistanceMeasureType = typeof otherAssistanceMeasureTypes[number]

/**
* Generated from evaka.core.assistance.OtherAssistanceMeasureUpdate
*/
export interface OtherAssistanceMeasureUpdate {
  type: OtherAssistanceMeasureType
  validDuring: FiniteDateRange
}

/**
* Generated from evaka.core.assistance.PreschoolAssistance
*/
export interface PreschoolAssistance {
  childId: PersonId
  id: PreschoolAssistanceId
  level: PreschoolAssistanceLevel
  modified: HelsinkiDateTime
  modifiedBy: EvakaUser
  validDuring: FiniteDateRange
}

/**
* Generated from evaka.core.assistance.PreschoolAssistanceLevel
*/
export const preschoolAssistanceLevels = [
  'INTENSIFIED_SUPPORT',
  'SPECIAL_SUPPORT',
  'SPECIAL_SUPPORT_WITH_DECISION_LEVEL_1',
  'SPECIAL_SUPPORT_WITH_DECISION_LEVEL_2',
  'CHILD_SUPPORT',
  'CHILD_SUPPORT_AND_EXTENDED_COMPULSORY_EDUCATION',
  'CHILD_SUPPORT_AND_OLD_EXTENDED_COMPULSORY_EDUCATION',
  'CHILD_SUPPORT_2_AND_OLD_EXTENDED_COMPULSORY_EDUCATION',
  'GROUP_SUPPORT'
] as const

export type PreschoolAssistanceLevel = typeof preschoolAssistanceLevels[number]

/**
* Generated from evaka.core.assistance.AssistanceController.PreschoolAssistanceResponse
*/
export interface PreschoolAssistanceResponse {
  data: PreschoolAssistance
  permittedActions: Action.PreschoolAssistance[]
}

/**
* Generated from evaka.core.assistance.PreschoolAssistanceUpdate
*/
export interface PreschoolAssistanceUpdate {
  level: PreschoolAssistanceLevel
  validDuring: FiniteDateRange
}


export function deserializeJsonAssistanceFactor(json: JsonOf<AssistanceFactor>): AssistanceFactor {
  return {
    ...json,
    modifiedAt: HelsinkiDateTime.parseIso(json.modifiedAt),
    validDuring: FiniteDateRange.parseJson(json.validDuring)
  }
}


export function deserializeJsonAssistanceFactorResponse(json: JsonOf<AssistanceFactorResponse>): AssistanceFactorResponse {
  return {
    ...json,
    data: deserializeJsonAssistanceFactor(json.data)
  }
}


export function deserializeJsonAssistanceFactorUpdate(json: JsonOf<AssistanceFactorUpdate>): AssistanceFactorUpdate {
  return {
    ...json,
    validDuring: FiniteDateRange.parseJson(json.validDuring)
  }
}


export function deserializeJsonAssistanceResponse(json: JsonOf<AssistanceResponse>): AssistanceResponse {
  return {
    ...json,
    assistanceActions: json.assistanceActions.map(e => deserializeJsonAssistanceActionResponse(e)),
    assistanceFactors: json.assistanceFactors.map(e => deserializeJsonAssistanceFactorResponse(e)),
    daycareAssistances: json.daycareAssistances.map(e => deserializeJsonDaycareAssistanceResponse(e)),
    otherAssistanceMeasures: json.otherAssistanceMeasures.map(e => deserializeJsonOtherAssistanceMeasureResponse(e)),
    preschoolAssistances: json.preschoolAssistances.map(e => deserializeJsonPreschoolAssistanceResponse(e))
  }
}


export function deserializeJsonDaycareAssistance(json: JsonOf<DaycareAssistance>): DaycareAssistance {
  return {
    ...json,
    modified: HelsinkiDateTime.parseIso(json.modified),
    validDuring: FiniteDateRange.parseJson(json.validDuring)
  }
}


export function deserializeJsonDaycareAssistanceResponse(json: JsonOf<DaycareAssistanceResponse>): DaycareAssistanceResponse {
  return {
    ...json,
    data: deserializeJsonDaycareAssistance(json.data)
  }
}


export function deserializeJsonDaycareAssistanceUpdate(json: JsonOf<DaycareAssistanceUpdate>): DaycareAssistanceUpdate {
  return {
    ...json,
    validDuring: FiniteDateRange.parseJson(json.validDuring)
  }
}


export function deserializeJsonOtherAssistanceMeasure(json: JsonOf<OtherAssistanceMeasure>): OtherAssistanceMeasure {
  return {
    ...json,
    modified: HelsinkiDateTime.parseIso(json.modified),
    validDuring: FiniteDateRange.parseJson(json.validDuring)
  }
}


export function deserializeJsonOtherAssistanceMeasureResponse(json: JsonOf<OtherAssistanceMeasureResponse>): OtherAssistanceMeasureResponse {
  return {
    ...json,
    data: deserializeJsonOtherAssistanceMeasure(json.data)
  }
}


export function deserializeJsonOtherAssistanceMeasureUpdate(json: JsonOf<OtherAssistanceMeasureUpdate>): OtherAssistanceMeasureUpdate {
  return {
    ...json,
    validDuring: FiniteDateRange.parseJson(json.validDuring)
  }
}


export function deserializeJsonPreschoolAssistance(json: JsonOf<PreschoolAssistance>): PreschoolAssistance {
  return {
    ...json,
    modified: HelsinkiDateTime.parseIso(json.modified),
    validDuring: FiniteDateRange.parseJson(json.validDuring)
  }
}


export function deserializeJsonPreschoolAssistanceResponse(json: JsonOf<PreschoolAssistanceResponse>): PreschoolAssistanceResponse {
  return {
    ...json,
    data: deserializeJsonPreschoolAssistance(json.data)
  }
}


export function deserializeJsonPreschoolAssistanceUpdate(json: JsonOf<PreschoolAssistanceUpdate>): PreschoolAssistanceUpdate {
  return {
    ...json,
    validDuring: FiniteDateRange.parseJson(json.validDuring)
  }
}
